import brush from './D3Brush/src/brush'
import { event as d3event, mouse as d3mouse, select } from 'd3-selection'
import findBlockInstanceByCoordinates from './findBlockInstanceByCoordinates'
import findSpaceByCoordinates from './findSpaceByCoordinates'
import findSpacesByAreaSelection from './findSpacesByAreaSelection'
import findMapObjectByCoordinates from './findMapObjectByCoordinates'
import findMapObjectsByAreaSelection from './findMapObjectsByAreaSelection'
import findBlockInstancesByAreaSelection from './findBlockInstancesByAreaSelection'
import findNeighborhoodByCoordinates from './findNeighborhoodByCoordinates'
import { isPin } from '@utils'

const SHIFT = 16
const ESC = 27

let brushing = false

class Selection {
  constructor(map) {
    this.map = map

    map.container.on('click', this.onClick)

    if (map.enableMultiSelection) {
      const w = select(window)

      w.on('keydown', () => {
        d3event.keyCode === SHIFT && !brushing && this.renderBush()
      }).on('keyup', () => {
        this.svg && this.removeBrush()
        d3event.keyCode === ESC && this.map.actions.deselectAll()
      })
    }
  }

  update = (map) => {
    this.map = map
  }

  destroy = () => {
    select(window).on('keydown', null)
    select(window).on('keyup', null)
  }

  onClick = () => {
    if (d3event.srcElement.id !== 'floorImage') return

    const {
      editingBlocks,
      selectedIds,
      enableMapObjectDrag,
      renumbering,
      nextSpaceCode,
      selectedItem,
      interactive,
      areas,
      container,
    } = this.map
    const mouse = d3mouse(container.node())
    const { shiftKey } = d3event

    const mapObject = editingBlocks
      ? findBlockInstanceByCoordinates(mouse, this.map)
      : findMapObjectByCoordinates(mouse, this.map)

    // Mobile map
    if (interactive) {
      if (mapObject) {
        window.postMessage(JSON.stringify(mapObject), '*')
      }
      return
    }

    // Edit map
    if (enableMapObjectDrag) {
      if (selectedItem) {
        return this.map.createMapObject()
      }

      if (mapObject && mapObject.spaceType && renumbering) {
        return this.map.actions.setSpaceCode({
          spaceId: mapObject.id,
          code: nextSpaceCode,
        })
      }

      if (mapObject) {
        return selectedIds[mapObject.id]
          ? this.map.actions.deselect(mapObject.id)
          : shiftKey
          ? this.map.actions.multiSelect([mapObject.id])
          : this.map.actions.select(mapObject.id)
      }

      this.map.actions.renumberOff()
      return
    }

    // All other maps
    // Handle click on space or pin
    if (mapObject) {
      if (isPin(mapObject)) {
        return
      }
      selectedIds[mapObject.id]
        ? this.map.actions.deselect(mapObject.id)
        : shiftKey
        ? this.map.actions.multiSelect([mapObject.id])
        : this.map.actions.select(mapObject.id)

      this.tooltip && this.tooltip.hideTooltip()

      return
    }

    // Handle click on neighborhood area
    if (areas) {
      const nh = findNeighborhoodByCoordinates(mouse, this.map)
      if (nh) {
        return this.map.actions.selectNeighborhood(nh.id)
      }
    }

    this.map.actions.deselectAll && this.map.actions.deselectAll()
  }

  renderBush = () => {
    brushing = true

    const { screen, container, tooltip, enableMapObjectDrag, editingBlocks } = this.map

    tooltip.hideTooltip()

    this.svg = container
      .append('svg')
      .attr('width', screen.w)
      .attr('height', screen.h)
      .style('position', 'absolute')
      .style('top', 0)
      .style('left', 0)
      .style('z-index', 100)
      .on('click', () => {
        // TEMP FIX: hide svg on click (or Esc key press - handle in this.onKeyup
        // Sometimes on Windows on keyup event doesn't get called
        // and SVG element block the map interactions...
        const mouse = d3mouse(this.map.container.node())
        const mapObject = findSpaceByCoordinates(mouse, this.map)
        this.removeBrush()
        mapObject && this.map.actions.multiSelect([mapObject.id])
      })

    this.svg.append('g').call(
      brush().on('end', () => {
        const mouse = d3mouse(this.map.container.node())
        const { selection } = d3event
        this.removeBrush()

        if (selection) {
          let ids

          if (editingBlocks) {
            ids = findBlockInstancesByAreaSelection(selection, this.map)
          } else if (enableMapObjectDrag) {
            ids = findMapObjectsByAreaSelection(selection, this.map)
          } else {
            ids = findSpacesByAreaSelection(selection, this.map)
          }

          if (ids) {
            const _ids = ids.reduce(
              (result, id) => (this.map.filteredIds[id] ? result : [...result, id]),
              []
            )

            _ids.length && this.map.actions.multiSelect(_ids)
          }
        } else {
          const mapObject = findSpaceByCoordinates(mouse, this.map)
          mapObject && this.map.actions.multiSelect([mapObject.id])
        }
      })
    )
  }

  removeBrush = () => {
    brushing = false
    this.svg && this.svg.remove()
  }
}

export default Selection
